// ===================================================
// ФАЙЛ: frontend/src/components/Common/EnhancedProgressBar.jsx
// ✅ УЛУЧШЕННЫЙ ПРОГРЕСС БАР - красивый, информативный и понятный
// 🚀 v2.0 - Добавлено: попытки скачивания, улучшенная индикация долгих процессов
// ===================================================

import React from 'react';
import { Progress, Typography, theme, Space, Tag, Tooltip } from 'antd';
import { Repeat, Clock, Timer, Zap, Hourglass } from 'lucide-react';
import { shouldShowBatchRow, formatEtaMinutes, resolveProcessRetry, formatProcessedCountLine, isLongRunningProcessMeta, PROCESS_HUNG_USER_LABEL, resolveProgressTrailColor, isCompletedProcessStatus } from '../../utils/processMonitorDisplay';

const { Text } = Typography;

/**
 * Улучшенный прогресс бар с детальной информацией
 * @param {Object} props
 * @param {number} props.percent - Процент выполнения (0-100)
 * @param {string} props.status - Статус процесса ('running', 'completed', 'failed', 'exception', etc.)
 * @param {string} props.stage - Текущий этап
 * @param {Object} props.metadata - Метаданные процесса
 * @param {number} props.metadata.itemsProcessed - Обработанные элементы
 * @param {number} props.metadata.totalItems - Всего элементов
 * @param {number} props.metadata.currentBatch - Текущий батч
 * @param {number} props.metadata.totalBatches - Всего батчей
 * @param {number} props.metadata.currentStage - Текущий этап
 * @param {number} props.metadata.totalStages - Всего этапов
 * @param {number} props.metadata.itemsPerSecond - Скорость обработки
 * @param {number} props.metadata.estimatedMinutesLeft - Оставшееся время в минутах
 * @param {number} props.metadata.elapsedMinutes - Прошедшее время
 * @param {number} props.metadata.retryCount - Текущая попытка (0 = первая)
 * @param {number} props.metadata.maxRetries - Максимум попыток
 * @param {boolean} props.metadata.isLongRunning - Долгий процесс (не считать зависшим)
 * @param {boolean} props.metadata.waitingForSupplier - Ожидание ответа от поставщика
 * @param {string} props.size - Размер прогресс бара ('small', 'default', 'large')
 * @param {boolean} props.showDetails - Показывать детальную информацию
 * @param {boolean} props.isStuck - Процесс завис
 * @param {string} props.processType - Тип процесса для определения допустимой длительности
 */
const EnhancedProgressBar = ({
  percent = 0,
  status = 'active',
  stage,
  metadata = {},
  size = 'small',
  showDetails = true,
  isStuck = false,
  processType,
  ...otherProps
}) => {
  const { token } = theme.useToken();
  
  const isLongRunningType = isLongRunningProcessMeta(metadata, processType);
  const actuallyStuck = Boolean(isStuck);
  const isComplete = isCompletedProcessStatus(status);

  // Нормализуем процент
  const safePercent = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));

  // 🆕 Информация о попытках
  const retry = resolveProcessRetry(metadata);
  const maxRetries = retry.max;
  const hasRetries = retry.show;

  // Определяем статус для прогресс бара
  const progressStatus = actuallyStuck 
    ? 'exception' 
    : status === 'failed' 
      ? 'exception' 
      : status === 'completed' 
        ? 'success' 
        : 'active';

  // 🚀 УЛУЧШЕНО: Цвета градиента с учетом попыток и долгих процессов
  const strokeColor = status === 'completed' 
    ? { from: token.colorSuccess, to: token.colorSuccess }
    : actuallyStuck
      ? { from: token.colorError, to: token.colorErrorBorder }
      : hasRetries
        ? { from: 'var(--warn)', to: 'var(--warn)' } // Оранжевый для процессов с ретраями
        : metadata.waitingForSupplier
          ? { from: '#722ed1', to: '#9254de' } // Фиолетовый для ожидания поставщика
          : { from: token.colorPrimary, to: token.colorInfo };

  // Форматирование времени
  const formatTime = (minutes) => {
    if (!minutes || minutes <= 0) return '0 мин';
    if (minutes < 60) return `${Math.round(minutes)} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}ч ${mins > 0 ? mins + 'м' : ''}` : `${mins} мин`;
  };

  // Форматирование скорости
  const formatSpeed = (itemsPerSecond) => {
    if (!itemsPerSecond || itemsPerSecond <= 0) return '—';
    if (itemsPerSecond < 1) return `${(itemsPerSecond * 60).toFixed(1)} в мин`;
    return `${itemsPerSecond.toFixed(1)} в сек`;
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Заголовок с этапом и процентом - ВСЕГДА показываем проценты */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 6 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {stage && (
            <Text 
              type="secondary" 
              style={{ 
                fontSize: size === 'small' ? 12 : 14, 
                fontWeight: 500 
              }}
            >
              {stage}
            </Text>
          )}
          {metadata.currentStage && metadata.totalStages && (
            <Tag size="small" color="processing">
              Этап {metadata.currentStage}/{metadata.totalStages}
            </Tag>
          )}
        </div>
        <Text 
          strong 
          style={{ 
            fontSize: size === 'small' ? 13 : 15, 
            color: progressStatus === 'exception' ? token.colorError : token.colorPrimary 
          }}
        >
          {safePercent}%
        </Text>
      </div>

      {/* 🆕 Индикация попыток скачивания */}
      {(hasRetries || metadata.waitingForSupplier) && (
        <div style={{ marginBottom: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {hasRetries && (
            <Tooltip title={`Выполняется повторная попытка загрузки. При ошибках система автоматически повторяет до ${maxRetries} раз.`}>
              <Tag 
                color="orange" 
                size="small"
                icon={<Repeat size={12} aria-hidden />}
              >
                Повтор {retry.count} из {maxRetries}
              </Tag>
            </Tooltip>
          )}
          {metadata.waitingForSupplier && (
            <Tooltip title="Поставщик формирует данные. Это может занять до 2 часов для больших каталогов.">
              <Tag 
                color="purple" 
                size="small"
                icon={<Hourglass size={12} aria-hidden />}
              >
                Ожидание поставщика
              </Tag>
            </Tooltip>
          )}
        </div>
      )}

      {/* Предупреждение о зависшем процессе (только для реально зависших) */}
      {actuallyStuck && (
        <div style={{ marginBottom: 4 }}>
          <Tag color="red" size="small">
            {PROCESS_HUNG_USER_LABEL}
          </Tag>
        </div>
      )}

      {/* U5: trail контрастнее контейнера карточки; percent≥1 задаёт resolveProcessCounts */}
      <Progress
        percent={safePercent}
        size={size}
        status={progressStatus}
        showInfo={false}
        strokeColor={strokeColor}
        trailColor={resolveProgressTrailColor(token)}
        strokeLinecap="round"
        strokeWidth={size === 'small' ? 8 : 10}
        aria-label={`Прогресс ${safePercent} процентов`}
        {...otherProps}
      />

      {/* Детальная информация */}
      {showDetails && (
        <div style={{ marginTop: 6 }}>
          {/* Основная статистика */}
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {/* Количество элементов */}
            {(() => {
              const countsLine = formatProcessedCountLine(metadata, status);
              if (!countsLine) return null;
              return (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {countsLine.doneCaption}: <Text strong style={{ color: token.colorText, fontSize: 12 }}>
                    {countsLine.doneText}
                  </Text>
                  {countsLine.totalText ? (
                    <> из <Text strong style={{ fontSize: 12 }}>{countsLine.totalText}</Text></>
                  ) : null}
                </Text>
                {countsLine.leftCaption ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {countsLine.leftCaption}: <Text strong style={{ color: countsLine.isComplete ? token.colorSuccess : token.colorWarning, fontSize: 12 }}>
                    {countsLine.leftText}
                  </Text>
                </Text>
                ) : null}
              </div>
              );
            })()}

            {/* Батчи: U11 — не показывать 1-SKU ARM remaining как «Батч 5836/547244» */}
            {!isComplete && shouldShowBatchRow(metadata) && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Батч <Text strong style={{ color: token.colorInfo }}>
                    {metadata.currentBatch ?? metadata.batch}
                  </Text> из <Text strong>
                    {metadata.totalBatches}
                  </Text>
                </Text>
                {metadata.batchSize && (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Размер: {Number(metadata.batchSize).toLocaleString('ru-RU')}
                  </Text>
                )}
              </div>
            )}

            {/* Время и скорость */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8
            }}>
              {/* Прошедшее время */}
              {metadata.elapsedMinutes != null && metadata.elapsedMinutes > 0 && (
                <Tooltip title="Время выполнения процесса">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <Clock size={12} style={{ marginRight: 3 }} aria-hidden />
                    Прошло: <Text strong style={{ color: token.colorText }}>
                      {formatTime(metadata.elapsedMinutes)}
                    </Text>
                  </Text>
                </Tooltip>
              )}
              
              {/* Оставшееся время */}
              {(() => {
                if (isComplete) return null;
                const etaLabel = formatEtaMinutes(metadata.estimatedMinutesLeft);
                if (!etaLabel) return null;
                return (
                <Tooltip title="Ориентировочное время до окончания">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <Timer size={12} style={{ marginRight: 3 }} aria-hidden />
                    Осталось: <Text strong style={{ color: token.colorSuccess }}>
                      {etaLabel}
                    </Text>
                  </Text>
                </Tooltip>
                );
              })()}
              
              {/* Скорость */}
              {!isComplete && metadata.itemsPerSecond != null && metadata.itemsPerSecond > 0 && (
                <Tooltip title="Скорость обработки">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <Zap size={12} style={{ marginRight: 3 }} aria-hidden />
                    {formatSpeed(metadata.itemsPerSecond)}
                  </Text>
                </Tooltip>
              )}
            </div>
            
            {/* 🆕 Информация о долгом процессе */}
            {!isComplete && isLongRunningType && metadata.elapsedMinutes > 30 && (
              <div style={{ 
                marginTop: 6, 
                padding: '4px 8px', 
                background: token.colorInfoBg, 
                borderRadius: 4,
                fontSize: 10
              }}>
                <Text type="secondary">
                  <Hourglass size={12} style={{ marginRight: 4 }} aria-hidden />
                  Долгий процесс - {metadata.waitingForSupplier 
                    ? 'ожидание формирования данных поставщиком' 
                    : 'обработка большого объема данных'}
                </Text>
              </div>
            )}
          </Space>
        </div>
      )}
    </div>
  );
};

export default EnhancedProgressBar;